import { zodResolver } from '@hookform/resolvers/zod';
import { differenceInDays, endOfMonth, startOfMonth } from 'date-fns';
import { Label } from 'react-aria-components';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import SimpleMultiSelect from '~/components/inputs/Select/SimpleMultiSelect';
import { snack } from '~/hooks/ToastProvider';
import { useErrorMap } from '~/hooks/forms/useErrorMap';
import { useAnalytics } from '~/hooks/useAnalytics';
import { captureException } from '~/hooks/useExceptionLogger';
import { ModalProps } from '~/modals/Modal';
import { AnalyticsEvents } from '~/services/analytics';
import { useExportInvoices } from '~/services/invoicing';
import { useLoggedUser } from '~/services/user';
import { formatDateForAPI } from '~/utils/formatDate';
import DateRangePicker from '../inputs/DatePicker/DateRangePicker';
import Radio from '../inputs/Radio/Radio';
import RadioGroup from '../inputs/RadioGroup/RadioGroup';
import TextField from '../inputs/TextField/TextField';
import { VStack } from '../layout/Stack';
import Modal from '../modals/Modal/Modal';
import ModalContent from '../modals/Modal/ModalContent';
import ModalTitle from '../modals/Modal/ModalTitle';
import SimpleModalActions from '../modals/Modal/SimpleModalActions';

enum ExportInvoicesKind {
  Bill = 'BILL',
  Received = 'RECEIVED',
  Issued = 'ISSUED',
  SelfInvoice = 'SELF_INVOICE',
}

const exportFormSchema = z.object({
  range: z.object({ start: z.date(), end: z.date() }).refine((x) => differenceInDays(x.end, x.start) < 93, {
    params: { issue: 'month_range_limit' },
  }),
  email: z.string().email(),
  kind: z.array(z.nativeEnum(ExportInvoicesKind)).min(1),
  format: z.string(),
});

type ExportForm = z.infer<typeof exportFormSchema>;

export const ExportInvoices = ({ closeModal }: ModalProps) => {
  const { t } = useTranslation('documents', { keyPrefix: 'export' });
  const { data: user } = useLoggedUser();

  const errorMap = useErrorMap([{ issue: 'month_range_limit', message: t('max_date_range') }]);
  const exportInvoices = useExportInvoices();
  const { track } = useAnalytics();
  const { control, handleSubmit } = useForm({
    defaultValues: {
      range: { start: startOfMonth(new Date()), end: endOfMonth(new Date()) },
      email: user?.email || '',
      kind: [ExportInvoicesKind.Bill, ExportInvoicesKind.Received, ExportInvoicesKind.Issued],
      format: 'RAW',
    },
    resolver: zodResolver(exportFormSchema, { error: errorMap }),
  });

  const submit = (val: ExportForm) => {
    const request = {
      from: formatDateForAPI(val.range.start),
      to: formatDateForAPI(val.range.end),
      requested: val.kind.map((x) => x.toString()),
      email: val.email,
      decode: val.format === 'CONVERT',
    };
    exportInvoices.mutate(request, {
      onSuccess: () => {
        track(AnalyticsEvents.DOCUMENTS_ARCHIVE_EXPORTED, {
          startDate: request.from,
          endDate: request.to,
          email: request.email,
        });

        snack.success(t('export_success'));
        closeModal();
      },
      onError: (err) => {
        captureException(err);
        snack.error(t('export_error'));
      },
    });
  };

  return (
    <Modal isOpen maxWidth={400} fullWidth>
      <ModalTitle onCloseClick={closeModal}>{t('title')}</ModalTitle>
      <ModalContent>
        <VStack gap={2}>
          <Controller
            control={control}
            name="range"
            render={({ fieldState, field }) => (
              <DateRangePicker
                isRequired
                isInvalid={fieldState.invalid}
                errorMessage={fieldState.error?.message}
                {...field}
                label={t('range.label')}
                helpText={t('range.help')}
              />
            )}
          />
          <Controller
            control={control}
            name="kind"
            render={({ fieldState, field }) => (
              <SimpleMultiSelect
                {...field}
                isRequired
                label={t('kind.label')}
                isInvalid={fieldState.invalid}
                errorMessage={fieldState.error?.message}
                options={[
                  {
                    value: ExportInvoicesKind.Received,
                    label: t('received'),
                  },
                  {
                    value: ExportInvoicesKind.Issued,
                    label: t('issued'),
                  },
                  {
                    value: ExportInvoicesKind.Bill,
                    label: t('bill'),
                  },
                  {
                    value: ExportInvoicesKind.SelfInvoice,
                    label: t('self_invoices'),
                  },
                ]}
                renderMultipleSelectionValue={(selected) => {
                  return selected.map((x) => x.label).join(', ');
                }}
              />
            )}
          />
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                isInvalid={fieldState.invalid}
                errorMessage={fieldState.error?.message}
                isRequired
                label={t('mail.label')}
              />
            )}
          />
          <div>
            <Label>{t('format.label')}</Label>

            <Controller
              control={control}
              name="format"
              render={({ field, fieldState }) => (
                <RadioGroup {...field} ariaLabel={t('format.label')} errorMessage={fieldState.error?.message}>
                  <Radio value="RAW">{t('format.keep')}</Radio>
                  <Radio value="CONVERT">{t('format.convert')}</Radio>
                </RadioGroup>
              )}
            />
          </div>
        </VStack>
      </ModalContent>
      <SimpleModalActions
        onOpenChange={(isOpen) => {
          if (!isOpen) closeModal();
        }}
        onConfirm={handleSubmit(submit)}
        confirmLabel={t('submit')}
        cancelLabel={t('cancel')}
      />
    </Modal>
  );
};
